import {memo, useCallback, useContext, useMemo, useRef, useState} from 'react'
import { notifyError } from '../../../../../utils/notifications'
import {useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {Form, Segmented, Typography} from 'antd'
import InputText from '../../../../common/InputText'
import {getRequest} from '../../../../../services/base'
import {NavContext} from '../../../../../context/global_context'
import {
    CLEAR_SELECTED_RECORD,
    SET_ACTIVE_SEARCH_TAB,
    SET_SEARCH_DATA,
    SET_SEARCHING_STATUS,
    SET_SELECTED_RECORD,
    useSearchContext,
} from '../context/context'
import SearchResultsTable from './SearchResultsTable'
import SearchResultsLoading from './SearchResultsLoading'
import SelectedRecordCard from './SelectedRecordCard'
import CircuitPinsTable from './CircuitPinsTable'
import EllipsisTooltip from '../../../../common/EllipsisTooltip'

const SearchSection = () => {
  const intl = useIntl()
  const { vehicle } = useParams()
  const [state, dispatch] = useSearchContext()
  const [, navDispatch] = useContext(NavContext)
  const [searchValue, setSearchValue] = useState('')
  const latestQueryRef = useRef('')

  const getSearchData = useCallback(async () => {
    const query = searchValue.trim()
    if (!query) {
      dispatch({
        type: SET_SEARCH_DATA,
        data: { connectors: [], devices: [], circuits: [] },
      })
      return
    }
    latestQueryRef.current = query
    dispatch({ type: SET_SEARCHING_STATUS, data: true })
    try {
      const res = await getRequest(
        `/api/vehicles/global-search/${vehicle}/?search=${query}`
      )
      if (latestQueryRef.current === query) {
        if (res?.status === false) {
          notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.searchError' }))
          return
        }
        dispatch({ type: SET_SEARCH_DATA, data: res.data })
      }
    } finally {
      if (latestQueryRef.current === query) {
        dispatch({ type: SET_SEARCHING_STATUS, data: false })
      }
    }
  }, [dispatch, vehicle, searchValue])

  const onSearchInputChange = useCallback(
    (value) => {
      setSearchValue(value)
      latestQueryRef.current = value.trim()
      if (!value.trim()) {
        dispatch({
          type: SET_SEARCH_DATA,
          data: { connectors: [], devices: [], circuits: [] },
        })
      }
    },
    [dispatch]
  )

  const selectRecord = useCallback(
    async (record, type) => {
      navDispatch({
        type: 'loader',
        data: { caption: intl.formatMessage({ id: 'functionalDiagram.fetchingCircuitPins' }), showLoader: true },
        notification: {},
      })

      let url = `/api/vehicles/component-search/${vehicle}/?connector=${record.name}&is_connector=true`
      if (type === 'devices') {
        url = `/api/vehicles/component-search/${vehicle}/?device=${record.name}`
      }
      let res = await getRequest(url)
      navDispatch({ type: 'loader', data: {}, notification: {} })
      if (res?.status === false) {
        notifyError(navDispatch, intl.formatMessage({ id: 'functionalDiagram.componentLoadError' }))
        return
      }
      dispatch({
        type: SET_SELECTED_RECORD,
        data: { ...record, type, data: res?.data },
      })

      navDispatch({ type: 'loader', data: {}, notification: {} })
    },
    [dispatch, navDispatch, vehicle]
  )

  const onSelectConnector = useCallback((record) => selectRecord(record, 'connectors'), [selectRecord])
  const onSelectDevice = useCallback((record) => selectRecord(record, 'devices'), [selectRecord])
  const onSelectCircuit = useCallback((record) => selectRecord(record, 'circuits'), [selectRecord])

  const segmentedOptions = useMemo(
    () => [
      { label: <span data-testid="fd-segment-option" data-tab="connectors"><EllipsisTooltip className='ellipsis'>{`${intl.formatMessage({ id: 'functionalDiagram.connectors' })} ${state.searchData?.connectors?.length ?? 0}`}</EllipsisTooltip></span>, value: 'connectors' },
      { label: <span data-testid="fd-segment-option" data-tab="devices"><EllipsisTooltip className='ellipsis'>{`${intl.formatMessage({ id: 'functionalDiagram.devices' })} ${state.searchData?.devices?.length ?? 0}`}</EllipsisTooltip></span>, value: 'devices' },
      { label: <span data-testid="fd-segment-option" data-tab="circuits"><EllipsisTooltip className='ellipsis'>{`${intl.formatMessage({ id: 'functionalDiagram.circuits' })} ${state.searchData?.circuits?.length ?? 0}`}</EllipsisTooltip></span>, value: 'circuits' },
    ],
    [state.searchData, intl]
  )

  const onClearRecord = useCallback(() => dispatch({ type: CLEAR_SELECTED_RECORD }), [dispatch])

  const onSegmentChange = useCallback((value) => {
    // setTimeout(() => {
      dispatch({ type: SET_ACTIVE_SEARCH_TAB, data: value })
    // }, 300)
  }, [dispatch])

  return (
    <div style={{ height: '100%' }}>
      <div
        className="col-body"
        style={{ display: state.selectedRecord ? 'flex' : 'none', height: '100%' }}
      >
        <SelectedRecordCard record={state.selectedRecord} onClear={onClearRecord} />
        <CircuitPinsTable />
      </div>

      <div
        className="col-body"
        style={{ display: state.selectedRecord ? 'none' : 'flex', height: '100%' }}
      >
        <Typography.Text className='bold'>
          <EllipsisTooltip className='ellipsis'>{intl.formatMessage({ id: 'functionalDiagram.searchInstruction' })}</EllipsisTooltip>
        </Typography.Text>

        <Form>
          <InputText
            type="search"
            name="search_value"
            placeholder={intl.formatMessage({ id: 'functionalDiagram.searchPlaceholder' })}
            isRequired={false}
value={searchValue}
          onChange={onSearchInputChange}
          onSubmit={getSearchData}
          />
        </Form>

        <Segmented
          options={segmentedOptions}
          value={state.activeSearchTab}
          onChange={onSegmentChange}
          block
          className="search-segmented"
        />

        {state.isSearching ? (
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <SearchResultsLoading type={state.activeSearchTab} />
          </div>
        ) : (
          <>
            <div
              style={{
                display: state.activeSearchTab === 'connectors' ? 'block' : 'none',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
              data-testid="fd-search-results"
              data-type="connectors"
            >
              <SearchResultsTable
                type="connectors"
                rows={state.searchData?.connectors}
                onSelect={onSelectConnector}
              />
            </div>
            <div
              style={{
                display: state.activeSearchTab === 'devices' ? 'block' : 'none',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
              data-testid="fd-search-results"
              data-type="devices"
            >
              <SearchResultsTable
                type="devices"
                rows={state.searchData?.devices}
                onSelect={onSelectDevice}
              />
            </div>
            <div
              style={{
                display: state.activeSearchTab === 'circuits' ? 'block' : 'none',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
              }}
              data-testid="fd-search-results"
              data-type="circuits"
            >
              <SearchResultsTable
                type="circuits"
                rows={state.searchData?.circuits}
                onSelect={onSelectCircuit}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default memo(SearchSection)
